"use client";

import {
  ScheduleBuffer,
  WorkplaceRequestApprovalResponse,
  WorkplaceRequestComment,
  WorkplaceRequestCommentAttachmentInput,
  WorkplaceRequestElement,
  WorkplaceRequestLocationValue,
  WorkplaceRequestOccupiedResourceAction,
  WorkplaceRequestRelated,
  WorkplaceRequestReleasedResourceAction,
  WorkplaceRequestWorkflowOverrides
} from "@gettactic/api";
import {
  capitalize,
  logError,
  useBooleanState,
  useDisclosure
} from "@gettactic/helpers";
import { getCategoryAssignedUsers } from "@gettactic/helpers/src/workplace-requests/categoryAssignees";
import { useAuthenticated, useI18n } from "@gettactic/hooks";
import { useEffect, useMemo, useState } from "react";
import type { ApproveRequestSelection } from "@/features/workplace-requests/components/ApproveRequestModal/ApproveRequestModal";
import { useInvalidateWorkplaceRequest } from "@/features/workplace-requests/hooks/useInvalidateWorkplaceRequest";
import { usePendingAttachments } from "@/features/workplace-requests/hooks/usePendingAttachments";
import { useWorkplaceRequestById } from "@/features/workplace-requests/hooks/useWorkplaceRequestById";
import { useWorkplaceRequestCategories } from "@/features/workplace-requests/hooks/useWorkplaceRequestCategories";
import {
  useWorkplaceRequestCommentMutations,
  useWorkplaceRequestComments
} from "@/features/workplace-requests/hooks/useWorkplaceRequestComments";
import { useWorkplaceRequestEvents } from "@/features/workplace-requests/hooks/useWorkplaceRequestEvents";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import { useWorkplaceRequestTypes } from "@/features/workplace-requests/hooks/useWorkplaceRequestTypes";
import { useWorkplaceRequestWorkflows } from "@/features/workplace-requests/hooks/useWorkplaceRequestWorkflows";
import { getApprovalErrorMessage } from "@/features/workplace-requests/lib/approvalError";
import { buildDeskWorkflowOverrides } from "@/features/workplace-requests/lib/desk-overrides";
import { resolveFollowUps } from "@/features/workplace-requests/lib/followUpRequests";
import { generateTicketUpload } from "@/features/workplace-requests/lib/ticketUpload";
import { isValidWorkplaceRequest } from "./request-guards";

interface UseRequestDetailControllerParams {
  requestId?: string;
  isPageMode: boolean;
  dontTouchUrl: boolean;
  initialRequest: WorkplaceRequestElement | null;
  onError?: (message: string) => void;
  onClose?: () => void;
  onRequestUpdate?: (updatedRequest: WorkplaceRequestElement) => void;
}

/**
 * All the state, data-fetching, effects and mutation handlers for the workplace
 * request detail modal. Extracted from `RequestDetailsPage` so the component is
 * left with rendering only (CLAUDE.md: separate UI from logic/data access).
 */
export function useRequestDetailController({
  requestId,
  isPageMode,
  dontTouchUrl,
  initialRequest,
  onError,
  onClose,
  onRequestUpdate
}: UseRequestDetailControllerParams) {
  const invalidateWorkplaceRequest = useInvalidateWorkplaceRequest();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [newComment, setNewComment] = useState("");
  // Files staged for the comment being written. Kept next to `newComment` so
  // both are cleared together once the comment is posted.
  const commentAttachments = usePendingAttachments({
    generateUpload: generateTicketUpload
  });
  const { reset: resetCommentAttachments } = commentAttachments;
  const isEditingDescription = useBooleanState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const deleteConfirm = useDisclosure();
  const editModal = useDisclosure();
  const shareModal = useDisclosure();
  const reservationModal = useDisclosure();
  // Why the last "Approve & book" failed, shown inside the modal so the
  // approver can adjust the reservation and retry without losing their edits.
  const [reservationApprovalError, setReservationApprovalError] = useState<
    string | null
  >(null);
  const { templates } = useI18n(authenticatedUser);
  // Combined Comments / Activity section — defaults to Comments.
  const [activityTab, setActivityTab] = useState<"comments" | "activity">(
    "comments"
  );
  const deleteCommentConfirm = useDisclosure();
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [pendingChanges, setPendingChanges] = useState({
    status: "",
    owner: "",
    type: "",
    priority: ""
  });

  // A linked request (a parent or one of the children) opened from inside the
  // modal. The opener still owns which request the modal was opened on — and
  // its prev/next list — so following a link is tracked here rather than
  // handed back to it: every surface that renders the modal gets the same
  // navigation without wiring any of it.
  //
  // The whole request is kept, not just its id: it is the snapshot the modal
  // paints from until the fetch for it lands, the same job `initialRequest`
  // does for the request the modal was opened on.
  const [linkedRequest, setLinkedRequest] =
    useState<WorkplaceRequestRelated | null>(null);
  useEffect(() => {
    setLinkedRequest(null);
  }, [requestId]);
  const isViewingLinkedRequest = !!linkedRequest;
  const activeRequestId = linkedRequest?.id ?? requestId;

  // A comment is drafted against the request on screen, so moving to another one
  // (prev/next, or following a link) has to leave the draft behind. Attachments
  // especially: they are uploaded the moment they are picked, so a staged file
  // carried over would be posted, silently, on a ticket it was never meant for.
  useEffect(() => {
    setNewComment("");
    resetCommentAttachments();
  }, [activeRequestId, resetCommentAttachments]);

  // Use the hook to fetch request data when in page mode or when no initial request is provided
  const {
    data: fetchedRequestData,
    isLoading: isRequestLoading,
    error: requestError
  } = useWorkplaceRequestById(activeRequestId || "");

  // Handle the case where API returns an array instead of a single element
  const fetchedRequest = Array.isArray(fetchedRequestData)
    ? fetchedRequestData[0]
    : fetchedRequestData;

  // Prefer freshly fetched data so edits made inside the modal reflect
  // immediately; fall back to a snapshot while the single-request query is
  // still loading. Which snapshot depends on what is being read: the opener's
  // for the request the modal was opened on, the row's own copy for a linked
  // one. There must always be one — in modal mode a null request unmounts the
  // dialog, so a gap here would blink the modal out of existence mid-navigation
  // and strand it for good if the fetch failed.
  const fallbackRequest = isViewingLinkedRequest
    ? linkedRequest
    : initialRequest;
  const request: WorkplaceRequestElement | null =
    (isValidWorkplaceRequest(fetchedRequest) ? fetchedRequest : null) ||
    fallbackRequest ||
    null;

  const { allElements: comments, isFetching: commentsLoading } =
    useWorkplaceRequestComments(request?.id || "", { limit: 100 });

  const { createComment, updateComment, deleteComment } =
    useWorkplaceRequestCommentMutations();

  const {
    updateWorkplaceRequestOwner,
    deleteWorkplaceRequest,
    updateWorkplaceRequest,
    updateWorkplaceRequestMutation,
    respondToApproval,
    respondToApprovalMutation
  } = useWorkplaceRequestMutations();

  // What approving would do — book a reservation, file follow-up requests.
  // Only asked for while the viewer can still approve, which is the one moment
  // any of it is theirs to change.
  const canApprovePending =
    !!request &&
    request.approval_status === "pending" &&
    request.capabilities.can_approve;
  const {
    workflows: approvalWorkflows,
    reservationWorkflows,
    isLoading: isLoadingReservationWorkflows
  } = useWorkplaceRequestWorkflows(request?.id || "", canApprovePending);
  const followUps = useMemo(
    () => resolveFollowUps(approvalWorkflows),
    [approvalWorkflows]
  );
  // Everything runs unless the approver says otherwise, so this tracks what
  // they dropped rather than what they kept. It lives here, not in the
  // checklist: the decision is made in the approval box but sent by whichever
  // handler ends up performing the approval.
  const [skippedWorkflowIds, setSkippedWorkflowIds] = useState<string[]>([]);
  // The buffer each reservation the approval would book is held with, as the
  // sidebar has it. It starts on what core resolved and is only sent when the
  // approver moved it, so an untouched request books exactly what it planned.
  const [bufferDrafts, setBufferDrafts] = useState<
    Record<string, ScheduleBuffer>
  >({});
  // What the approver decided should happen to each freed desk, when they
  // disagreed with the request type's own setting. Absent means "whatever the
  // type says", so only the rules they actually touched are ever sent.
  const [releasedDeskOverrides, setReleasedDeskOverrides] = useState<
    Record<string, WorkplaceRequestReleasedResourceAction>
  >({});
  // What the approver decided should happen when a dedicated move's desk is
  // already full, for the rules where they disagreed with the type.
  const [occupiedDeskOverrides, setOccupiedDeskOverrides] = useState<
    Record<string, WorkplaceRequestOccupiedResourceAction>
  >({});
  // Navigating to another request (prev/next, or following a link) must not
  // carry the previous approver's choices over to a different ticket.
  useEffect(() => {
    setSkippedWorkflowIds([]);
    setBufferDrafts({});
    setReleasedDeskOverrides({});
    setOccupiedDeskOverrides({});
  }, [activeRequestId]);

  // Seed a draft per workflow once its resolved values arrive, leaving drafts
  // the approver has already touched alone.
  useEffect(() => {
    if (reservationWorkflows.length === 0) return;
    setBufferDrafts((current) => {
      let hasNew = false;
      const next = { ...current };
      for (const workflow of reservationWorkflows) {
        if (workflow.id in next) continue;
        next[workflow.id] = workflow.resolved?.buffer ?? {
          before_minutes: 0,
          after_minutes: 0
        };
        hasNew = true;
      }
      return hasNew ? next : current;
    });
  }, [reservationWorkflows]);

  const setBufferDraft = (workflowId: string, buffer: ScheduleBuffer) =>
    setBufferDrafts((current) => ({ ...current, [workflowId]: buffer }));

  const handleToggleFollowUp = (workflowId: string) => {
    setSkippedWorkflowIds((previous) =>
      previous.includes(workflowId)
        ? previous.filter((id) => id !== workflowId)
        : [...previous, workflowId]
    );
  };

  const handleReleasedDeskChange = (
    workflowId: string,
    action: WorkplaceRequestReleasedResourceAction
  ) => {
    setReleasedDeskOverrides((previous) => ({
      ...previous,
      [workflowId]: action
    }));
  };

  const handleOccupiedDeskChange = (
    workflowId: string,
    action: WorkplaceRequestOccupiedResourceAction
  ) => {
    setOccupiedDeskOverrides((previous) => ({
      ...previous,
      [workflowId]: action
    }));
  };

  /** The desk choices as core wants them; see buildDeskWorkflowOverrides. */
  const deskWorkflowOverrides = (): WorkplaceRequestWorkflowOverrides =>
    buildDeskWorkflowOverrides(
      releasedDeskOverrides,
      occupiedDeskOverrides,
      skippedWorkflowIds
    );

  const { data: eventsData, isFetching: eventsLoading } =
    useWorkplaceRequestEvents(request?.id || "", { limit: 50 });

  // These lists double as a lookup table for the current ticket's type/category
  // (e.g. resolving assignees), so both must cover every one. Ticket types come
  // back complete in a single page; categories still need an explicit `limit`,
  // or one outside the backend's default page size silently fails to resolve.
  const { allElements: workplaceRequestTypes } = useWorkplaceRequestTypes();
  const categories = useWorkplaceRequestCategories({ limit: 100 });

  // Handle request error
  useEffect(() => {
    if (requestError && isPageMode) {
      onError?.("Request not found or unavailable");
    }
  }, [requestError, isPageMode, onError]);

  useEffect(() => {
    if (
      !dontTouchUrl &&
      request &&
      window.location.pathname.split("/").pop() !== String(request?.display_id)
    ) {
      if (!isPageMode) {
        // add a /:display_id to the url so user can copy it to share
        // later details page can be opened from the link
        window.history.pushState(
          null,
          "",
          `/workplace-requests/${request?.display_id}`
        );
      }
    }
  }, [request, isPageMode]);

  useEffect(() => {
    if (request) {
      setEditedDescription(request.description || "");
      setPendingChanges({
        status: capitalize(request.status),
        owner: request.owner_user?.id || "",
        type: request.type.id,
        priority: request.priority ? String(request.priority) : ""
      });
    }
  }, [request]);

  // `onRequestUpdate` is how the opener keeps its own copy of the request it
  // opened in step with edits made here. A linked request is a different
  // ticket, so handing it over would make the opener select it — and since
  // openers drive `requestId` from that selection, the modal would then treat
  // the linked request as the opened one: its place in the list, and the
  // reservation the opener passed for a different ticket, would both come back
  // wrongly attached to it. The invalidations every handler already fires keep
  // the linked request itself up to date.
  const reportRequestUpdate = (updated: WorkplaceRequestElement) => {
    if (isViewingLinkedRequest) return;
    onRequestUpdate?.(updated);
  };

  const handleSubmitComment = async () => {
    const trimmedComment = newComment.trim();
    const attachments = commentAttachments.toInput();
    // A comment needs text or at least one uploaded file — the backend rejects
    // an empty one with `empty_comment`.
    if (!request || (!trimmedComment && attachments.length === 0)) return;

    try {
      await createComment.mutateAsync({
        ticketId: request.id,
        params: {
          comment: trimmedComment,
          ...(attachments.length > 0 ? { attachments } : {})
        }
      });
      setNewComment("");
      commentAttachments.reset();
      // Surface the new comment even if the viewer was on the Activity tab.
      setActivityTab("comments");
    } catch (error) {
      logError("Error creating comment:", error);
    }
  };

  const handleEditComment = async (
    commentId: string,
    // Omitted when the viewer only edited the text: the backend then keeps the
    // attachments the comment already has.
    attachments?: WorkplaceRequestCommentAttachmentInput[]
  ) => {
    const trimmedComment = editingCommentText.trim();
    const keepsAnAttachment = attachments
      ? attachments.length > 0
      : (comments.find((item) => item.id === commentId)?.attachments.length ??
          0) > 0;
    if (!request || (!trimmedComment && !keepsAnAttachment)) return;

    try {
      await updateComment.mutateAsync({
        ticketId: request.id,
        commentId,
        params: {
          comment: trimmedComment,
          ...(attachments ? { attachments } : {})
        }
      });
      setEditingCommentId(null);
      setEditingCommentText("");
    } catch (error) {
      logError("Error updating comment:", error);
    }
  };

  const handleDeleteComment = async () => {
    if (!request || !commentToDelete) return;

    try {
      await deleteComment.mutateAsync({
        ticketId: request.id,
        commentId: commentToDelete
      });
      deleteCommentConfirm.close();
      setCommentToDelete(null);
    } catch (error) {
      logError("Error deleting comment:", error);
    }
  };

  const handleStartEditComment = (comment: WorkplaceRequestComment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const handleConfirmDeleteComment = (commentId: string) => {
    setCommentToDelete(commentId);
    deleteCommentConfirm.open();
  };

  // `status` is a base status (open/closed/pending/archived) or a ticket-type sub-status id;
  // the backend resolves a sub-status id to its base status.
  const handleStatusChange = async (
    requestIdArg: string,
    status: string,
    comment?: string
  ) => {
    if (!request) return;

    try {
      await updateWorkplaceRequest(requestIdArg, {
        status,
        ...(comment ? { update_comment: comment } : {})
      });

      // Refresh the ticket (its sub_status) and the activity feed.
      invalidateWorkplaceRequest(requestIdArg);
    } catch (error) {
      logError("Error updating status:", error);
    }
  };

  const handleApprovalResponse = async (
    status: WorkplaceRequestApprovalResponse
  ) => {
    if (!request) return;

    // A reservation is the one thing approving does that the approver has to
    // fill in rather than merely agree to, so it gets a modal. Follow-up
    // requests don't: they are already listed, with their checkboxes, in the
    // approval box itself.
    if (status === "approve" && reservationWorkflows.length > 0) {
      setReservationApprovalError(null);
      reservationModal.open();
      return;
    }

    try {
      // Core rejects overrides alongside a rejection, and a rejection runs
      // nothing there would be a desk to free from.
      const deskOverrides = status === "approve" ? deskWorkflowOverrides() : {};

      await respondToApproval(
        request.id,
        status,
        Object.keys(deskOverrides).length > 0 ? deskOverrides : undefined,
        // Only an approval creates anything to skip; core rejects the pair
        // alongside a rejection.
        status === "approve" ? { skipWorkflowIds: skippedWorkflowIds } : {}
      );

      // Refresh the ticket (its approval_status) and the activity feed.
      invalidateWorkplaceRequest(request.id);
    } catch (error) {
      logError("Error responding to approval:", error);
    }
  };

  const handleApproveWithSelection = async ({
    overrides
  }: ApproveRequestSelection) => {
    if (!request) return;

    setReservationApprovalError(null);
    try {
      // The modal only ever edits reservations, so the desk choices made back
      // in the approval box have to be carried in alongside them.
      const allOverrides = {
        ...deskWorkflowOverrides(),
        ...overrides
      };

      await respondToApproval(
        request.id,
        "approve",
        Object.keys(allOverrides).length > 0 ? allOverrides : undefined,
        { showErrorToast: false, skipWorkflowIds: skippedWorkflowIds }
      );
      invalidateWorkplaceRequest(request.id);
      reservationModal.close();
    } catch (error) {
      logError("Error approving with workflow selection:", error);
      setReservationApprovalError(
        getApprovalErrorMessage(error, templates.displayDate)
      );
    }
  };

  const handleCloseReservationModal = () => {
    setReservationApprovalError(null);
    reservationModal.close();
  };

  // A linked request's status, changed from the parent's list without opening
  // it. The parent carries its children inline, so its own detail has to be
  // refetched for the row to catch up — invalidating the child alone would
  // leave the list showing the previous status.
  const handleLinkedStatusChange = async (linkedId: string, status: string) => {
    if (!request) return;

    try {
      await updateWorkplaceRequest(linkedId, { status });
      invalidateWorkplaceRequest(linkedId);
      invalidateWorkplaceRequest(request.id);
    } catch (error) {
      logError("Error updating linked request status:", error);
      throw error;
    }
  };

  const handleOpenLinkedRequest = (linked: WorkplaceRequestRelated) => {
    // A child's parent card can lead back to the request the modal was opened
    // on. Arriving there is not linked reading any more, so the snapshot is
    // dropped rather than replaced — that hands the request its place in the
    // opener's list back, and with it the prev/next controls.
    setLinkedRequest(linked.id === requestId ? null : linked);
  };

  const handleOwnerChange = async (ownerId: string) => {
    if (!request) return;

    setPendingChanges((prev) => ({ ...prev, owner: ownerId }));

    try {
      await updateWorkplaceRequestOwner(
        request.id,
        ownerId === "no-owner" ? null : ownerId
      );

      // `type.assigned_users` is an admin-only shim (see Sidebar's assignee
      // list), so resolve the picked user the same way: from the ticket's
      // category membership, which every viewer — not just admins — has.
      const selectedType = workplaceRequestTypes.find(
        (t) => t.id === request?.type.id
      );
      const selectedCategory = categories.allElements.find(
        (c) => c.id === selectedType?.category_id
      );
      const assignedUsers = getCategoryAssignedUsers(
        selectedCategory,
        request.office_id
      );
      const newOwner =
        ownerId === "no-owner"
          ? undefined
          : assignedUsers.find((user) => user.id === ownerId);

      const updatedRequest = {
        ...request,
        owner_user: newOwner || null
      };

      reportRequestUpdate(updatedRequest);
    } catch (error) {
      logError("Error updating owner:", error);

      setPendingChanges((prev) => ({
        ...prev,
        owner: request.owner_user?.id || ""
      }));
    }
  };

  const handleTypeChange = async (typeId: string) => {
    if (!request) return;

    setPendingChanges((prev) => ({ ...prev, type: typeId }));

    try {
      await updateWorkplaceRequest(request.id, {
        type_id: typeId
      });

      const selectedType = workplaceRequestTypes.find((t) => t.id === typeId);
      const updatedRequest = {
        ...request,
        type: {
          id: typeId,
          name: selectedType?.name || "",
          description: selectedType?.description || "",
          llm_context: selectedType?.llm_context || "",
          in_use: selectedType?.in_use || false
        }
      };

      reportRequestUpdate(updatedRequest);
    } catch (error) {
      logError("Error updating request type:", error);

      setPendingChanges((prev) => ({ ...prev, type: request.type.id }));
    }
  };

  const handlePriorityChange = async (priority: string) => {
    if (!request) return;

    const previous = pendingChanges.priority;
    setPendingChanges((prev) => ({ ...prev, priority }));

    try {
      const numeric = Number(priority);
      await updateWorkplaceRequest(request.id, {
        priority: (numeric === 1 || numeric === 2 || numeric === 3
          ? numeric
          : null) as 1 | 2 | 3 | null
      });

      reportRequestUpdate({ ...request, priority: numeric });
    } catch (error) {
      logError("Error updating priority:", error);
      setPendingChanges((prev) => ({ ...prev, priority: previous }));
    }
  };

  const handleLocationChange = async (
    location: WorkplaceRequestLocationValue
  ) => {
    if (!request) return;

    try {
      await updateWorkplaceRequest(request.id, { location });
      reportRequestUpdate({ ...request, location });

      // Approval gating (needsLocationBeforeApproval in Sidebar) reads
      // `request.location` off the fetched copy, which `onRequestUpdate`
      // above doesn't touch — refresh it the same way status/approval do.
      invalidateWorkplaceRequest(request.id);
    } catch (error) {
      logError("Error updating request location:", error);
    }
  };

  const handleSaveDescription = async () => {
    if (!request) return;

    try {
      await updateWorkplaceRequest(request.id, {
        description: editedDescription
      });

      const updatedRequest = {
        ...request,
        description: editedDescription
      };

      reportRequestUpdate(updatedRequest);

      isEditingDescription.false();
    } catch (error) {
      logError("Error updating description:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditedDescription(request?.description || "");
    isEditingDescription.false();
  };

  const handleStartEdit = () => {
    isEditingDescription.true();
  };

  const handleDeleteRequest = async () => {
    if (!request) return;

    try {
      await deleteWorkplaceRequest(request.id);
      deleteConfirm.close();

      setTimeout(() => {
        onClose?.();
      }, 100);
    } catch (error) {
      logError("Error deleting request:", error);
    }
  };

  const handleArchive = async () => {
    if (!request) return;
    try {
      await updateWorkplaceRequest(request.id, { status: "archived" });
      setTimeout(() => onClose?.(), 100);
    } catch (error) {
      logError("Error archiving request:", error);
    }
  };

  // Shared close handler: mirrors the Dialog's onOpenChange so the custom close
  // control in the sidebar behaves identically (keeps the URL in sync).
  const handleModalClose = () => {
    if (!isPageMode && !dontTouchUrl) {
      window.history.pushState(null, "", "/workplace-requests");
    }
    onClose?.();
  };

  return {
    // data
    request,
    isRequestLoading,
    authenticatedUser,
    comments,
    commentsLoading,
    eventsData,
    eventsLoading,
    workplaceRequestTypes,
    categories,
    createComment,
    updateComment,
    deleteComment,
    updateWorkplaceRequestMutation,
    respondToApprovalMutation,
    reservationWorkflows,
    followUps,
    skippedWorkflowIds,
    bufferDrafts,
    setBufferDraft,
    releasedDeskOverrides,
    occupiedDeskOverrides,
    isLoadingReservationWorkflows,
    isViewingLinkedRequest,
    // state
    newComment,
    setNewComment,
    commentAttachments,
    isEditingDescription,
    editedDescription,
    setEditedDescription,
    deleteConfirm,
    editModal,
    shareModal,
    reservationModal,
    reservationApprovalError,
    activityTab,
    setActivityTab,
    deleteCommentConfirm,
    editingCommentId,
    editingCommentText,
    setEditingCommentText,
    pendingChanges,
    // handlers
    handleSubmitComment,
    handleEditComment,
    handleDeleteComment,
    handleStartEditComment,
    handleCancelEditComment,
    handleConfirmDeleteComment,
    handleStatusChange,
    handleApprovalResponse,
    handleApproveWithSelection,
    handleToggleFollowUp,
    handleReleasedDeskChange,
    handleOccupiedDeskChange,
    handleCloseReservationModal,
    handleLinkedStatusChange,
    handleOpenLinkedRequest,
    handleOwnerChange,
    handleTypeChange,
    handlePriorityChange,
    handleLocationChange,
    handleSaveDescription,
    handleCancelEdit,
    handleStartEdit,
    handleDeleteRequest,
    handleArchive,
    handleModalClose
  };
}
